import React, { useState, useEffect } from 'react'
import { Table, Input, Typography, Tag, Card, Row, Col, Spin, Space, Button, App } from 'antd'
import { SearchOutlined, MedicineBoxOutlined, ReloadOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { listHerbs, type HerbInfo } from '../../api/herbs'
import { extractErrorMessage } from '../../api/responseAdapter'
import './Herbs.css'

const { useApp } = App
const { Title, Paragraph } = Typography

const Herbs: React.FC = () => {
  const { message } = useApp()
  const [herbs, setHerbs] = useState<HerbInfo[]>([])
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [inputValue, setInputValue] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>()
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100, // 增加默认每页显示数量
    total: 0,
  })

  // 加载中药数据
  const loadHerbs = async (page = 1, pageSize = 100, search?: string, category?: string) => {
    setLoading(true)
    try {
      const data = await listHerbs({
        skip: (page - 1) * pageSize,
        limit: pageSize,
        search,
        category,
      })
      setHerbs(data.herbs || [])
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize,
        total: data.total || 0,
      }))
    } catch (error: unknown) {
      console.error('加载中药数据失败:', error)
      message.error(extractErrorMessage(error))
      setHerbs([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadHerbs(pagination.current, pagination.pageSize, searchValue, categoryFilter)
  }, [])

  const handleSearch = (value: string) => {
    setSearchValue(value)
    setPagination(prev => ({ ...prev, current: 1 }))
    loadHerbs(1, pagination.pageSize, value, categoryFilter)
  }

  const handleTableChange = (page: number, pageSize: number) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }))
    loadHerbs(page, pageSize, searchValue, categoryFilter)
  }

  const handleRefresh = () => {
    loadHerbs(pagination.current, pagination.pageSize, searchValue, categoryFilter)
  }

  // 表格列定义
  const columns: ColumnsType<HerbInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      fixed: 'left',
    },
    {
      title: '中药名',
      dataIndex: 'chinese_name',
      key: 'chinese_name',
      width: 120,
      fixed: 'left',
      render: (text) => <strong>{text || '-'}</strong>,
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      render: (text) => text ? <Tag color="blue">{text}</Tag> : '-',
    },
    {
      title: '拼音',
      dataIndex: 'pinyin',
      key: 'pinyin',
      width: 120,
    },
    {
      title: '拉丁名',
      dataIndex: 'latin_name',
      key: 'latin_name',
      width: 150,
    },
    {
      title: '拉丁学名',
      dataIndex: 'scientific_name',
      key: 'scientific_name',
      width: 180,
    },
    {
      title: '性味',
      dataIndex: 'nature_flavor',
      key: 'nature_flavor',
      width: 120,
    },
    {
      title: '归经',
      dataIndex: 'meridian',
      key: 'meridian',
      width: 150,
    },
    {
      title: '功效',
      dataIndex: 'functions',
      key: 'functions',
      width: 200,
      ellipsis: true,
    },
    {
      title: '主治',
      dataIndex: 'indications',
      key: 'indications',
      width: 250,
      ellipsis: true,
    },
    {
      title: '用法用量',
      dataIndex: 'usage_dosage',
      key: 'usage_dosage',
      width: 120,
    },
    {
      title: '药用部位',
      dataIndex: 'medicinal_part',
      key: 'medicinal_part',
      width: 120,
    },
    {
      title: '分布地区',
      dataIndex: 'distribution',
      key: 'distribution',
      width: 200,
      ellipsis: true,
    },
  ]

  return (
    <div className="herbs-page">
      <div className="herbs-hero">
        <div className="section-container">
          <Title level={1} className="page-title">中药库</Title>
          <Paragraph className="page-subtitle">
            汇聚传统中药智慧，提供全面的中药信息查询与检索服务
          </Paragraph>
          <Space size="large" style={{ marginTop: 24 }}>
            <Space.Compact style={{ width: 400 }}>
              <Input
                placeholder="搜索中药名、拼音或拉丁名..."
                size="large"
                style={{ width: 'calc(100% - 40px)' }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onPressEnter={() => handleSearch(inputValue)}
                allowClear
                onClear={() => {
                  setInputValue('')
                  handleSearch('')
                }}
              />
              <Button
                size="large"
                icon={<SearchOutlined />}
                onClick={() => handleSearch(inputValue)}
                type="primary"
              />
            </Space.Compact>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        </div>
      </div>

      <div className="section-container">
        <Card>
          {loading && herbs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>正在加载中药数据...</div>
            </div>
          ) : herbs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div>暂无数据</div>
              <div style={{ marginTop: 8, color: '#999' }}>
                请确保后端服务运行在 http://localhost:8001
              </div>
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={herbs}
              rowKey="id"
              loading={loading}
              pagination={{
                current: pagination.current,
                pageSize: pagination.pageSize,
                total: pagination.total,
                showSizeChanger: true,
                showTotal: (total) => `共 ${total} 条记录`,
                pageSizeOptions: ['20', '50', '100', '200', '500'],
                showQuickJumper: true,
              }}
              onChange={(pagination) => {
                handleTableChange(pagination.current || 1, pagination.pageSize || 20)
              }}
              scroll={{ x: 1500 }}
              size="middle"
            />
          )}
        </Card>
      </div>
    </div>
  )
}

export default Herbs

